import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { assetUrl } from '../lib/assetUrl';
import axios from 'axios';
import { LayoutDashboard, Users, Calendar, Settings, LogOut, Menu, X, Package, Building2, UserCog, FileBarChart, Tags, BoxesIcon, ChevronDown, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Layout = ({ children }) => {
  const { user, logout, login: setAuth } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyBrand, setCompanyBrand] = useState({ company_name: '', logo_url: '' });
  const [companies, setCompanies] = useState([]);
  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/company-settings`).then(res => {
      setCompanyBrand(res.data);
    }).catch((err) => console.warn('[Layout]', err));
  }, [user?.company_id]);

  useEffect(() => {
    const ids = user?.company_ids;
    if ((ids && ids.length > 1) || user?.role === 'system_admin') {
      axios.get(`${API}/my-companies`).then(res => {
        setCompanies(res.data);
      }).catch((err) => console.warn('[Layout]', err));
    }
  }, [user?.company_ids, user?.role]);

  const switchCompany = async (companyId) => {
    try {
      const res = await axios.post(`${API}/auth/switch-company`, { company_id: companyId });
      localStorage.setItem('token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      setAuth(res.data.token, res.data.user);
      setCompanySwitcherOpen(false);
    } catch (err) { console.warn('[Layout]', err); }
  };

  const showCompanySwitcher = companies.length > 0;

  useEffect(() => {
    const refetch = () => axios.get(`${API}/company-settings`).then(res => {
      setCompanyBrand(res.data);
    }).catch((err) => console.warn('[Layout]', err));
    refetch();
    window.addEventListener('company-settings-updated', refetch);
    return () => window.removeEventListener('company-settings-updated', refetch);
  }, []);

  const isLager = user?.role === 'lager';
  const isKunde = user?.role === 'kunde';
  const pageAccess = user?.page_access;

  const allPages = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Kunder', path: '/customers', icon: Users },
    { name: 'Rens Planlegging', path: '/cleaning', icon: Calendar },
    { name: 'Montering', path: '/montering', icon: Wrench },
    { name: 'Utlånt Utstyr', path: '/loaned-equipment', icon: Package },
    { name: 'Rapporter', path: '/reports', icon: FileBarChart },
  ];

  let navigation;
  if (isLager) {
    navigation = [{ name: 'Utlånt Utstyr', path: '/loaned-equipment', icon: Package }];
  } else if (pageAccess && pageAccess.length > 0) {
    navigation = allPages.filter(p => pageAccess.includes(p.path));
  } else {
    navigation = [...allPages];
  }

  // Admin menu items
  if (!isLager && !isKunde && (user?.role === 'admin' || user?.role === 'system_admin')) {
    if (!pageAccess || pageAccess.length === 0 || pageAccess.includes('/admin'))
      navigation.push({ name: 'Brukere', path: '/admin', icon: UserCog });
  }

  // System admin only
  if (user?.role === 'system_admin') {
    navigation.push({ name: 'Selskaper', path: '/system-admin', icon: Building2 });
  }

  // Settings for everyone
  navigation.push({ name: 'Innstillinger', path: '/settings', icon: Settings });

  const getRoleName = (role) => {
    switch (role) {
      case 'system_admin': return 'System Admin';
      case 'admin': return 'Admin';
      case 'tekniker': return 'Tekniker';
      case 'lager': return 'Lager';
      default: return role;
    }
  };

  const bottomNav = [
    { name: 'Hjem', path: '/', icon: LayoutDashboard },
    { name: 'Rens', path: '/cleaning', icon: Calendar },
    { name: 'Kunder', path: '/customers', icon: Users },
    { name: 'Montering', path: '/montering', icon: Wrench },
    { name: 'Utlån', path: '/loaned-equipment', icon: Package },
  ].filter((i) => navigation.some((n) => n.path === i.path));
  const showBottomNav = bottomNav.length >= 2;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-[100dvh] bg-slate-50">
      {/* Sidebar - Desktop */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow pt-5 overflow-y-auto bg-white border-r border-slate-200">
          <div className="flex items-center flex-shrink-0 px-6 gap-3">
            {companyBrand.logo_url && (
              <img src={assetUrl(companyBrand.logo_url)} alt="" className="h-8 w-8 object-contain rounded" />
            )}
            <h1 className="text-xl font-bold text-slate-900 truncate" data-testid="sidebar-brand-name" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {companyBrand.company_name || 'TapService Pro'}
            </h1>
          </div>
          <div className="mt-8 flex-grow flex flex-col">
            <nav className="flex-1 px-4 space-y-2">
              {navigation.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                    className={`sidebar-link group flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                      active
                        ? 'active bg-brand-600 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${active ? 'text-white' : 'text-slate-500'}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 border-t border-slate-200">
            {showCompanySwitcher && (
              <div className="px-4 pt-3 relative">
                <button
                  onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="company-switcher-btn"
                >
                  <span className="truncate">{companies.find(c => c.id === user?.company_id)?.name || 'Velg selskap'}</span>
                  <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${companySwitcherOpen ? 'rotate-180' : ''}`} />
                </button>
                {companySwitcherOpen && (
                  <div className="absolute bottom-full left-4 right-4 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                    {companies.map(c => (
                      <button
                        key={c.id}
                        onClick={() => switchCompany(c.id)}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors ${c.id === user?.company_id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-slate-700'}`}
                        data-testid={`switch-company-${c.id}`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                  <p className="text-xs text-slate-500">{getRoleName(user?.role)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logout}
                  data-testid="logout-button"
                  className="text-slate-500 hover:text-slate-700"
                >
                  <LogOut className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex md:hidden">
          <div className="fixed inset-0 bg-slate-900 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 flex items-center px-6 gap-3">
                {companyBrand.logo_url && (
                  <img src={assetUrl(companyBrand.logo_url)} alt="" className="h-8 w-8 object-contain rounded" />
                )}
                <h1 className="text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {companyBrand.company_name || 'TapService Pro'}
                </h1>
              </div>
              <nav className="mt-8 px-4 space-y-2">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`sidebar-link group flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                        active
                          ? 'active bg-brand-600 text-white'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className={`mr-3 h-5 w-5 ${active ? 'text-white' : 'text-slate-500'}`} />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 border-t border-slate-200">
              {showCompanySwitcher && (
                <div className="px-4 pt-3 relative">
                  <button
                    onClick={() => setCompanySwitcherOpen(!companySwitcherOpen)}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid="mobile-company-switcher-btn"
                  >
                    <span className="truncate">{companies.find(c => c.id === user?.company_id)?.name || 'Velg selskap'}</span>
                    <ChevronDown className={`h-3.5 w-3.5 ml-1 transition-transform ${companySwitcherOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {companySwitcherOpen && (
                    <div className="absolute bottom-full left-4 right-4 mb-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50">
                      {companies.map(c => (
                        <button
                          key={c.id}
                          onClick={() => { switchCompany(c.id); setSidebarOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${c.id === user?.company_id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-slate-700'}`}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between w-full">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                    <p className="text-xs text-slate-500">{getRoleName(user?.role)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500">
                    <LogOut className="h-5 w-5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden px-3 pt-4 pb-2 bg-white border-b border-slate-200 flex items-center gap-2" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
          <button
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-button"
            className="h-12 w-12 inline-flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <Menu className="h-7 w-7" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            {companyBrand.logo_url && (
              <img src={assetUrl(companyBrand.logo_url)} alt="" className="h-8 w-8 object-contain rounded" />
            )}
            <span className="text-base font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
              {companyBrand.company_name || 'TapService Pro'}
            </span>
          </div>
        </div>
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className={`py-5 sm:py-6 px-4 sm:px-6 lg:px-8 md:pb-20 ${showBottomNav ? 'pb-[calc(6rem+env(safe-area-inset-bottom))]' : 'pb-[calc(3rem+env(safe-area-inset-bottom))]'}`}>
            {children}
          </div>
        </main>
        {showBottomNav && (
          <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-2px_12px_rgba(15,23,42,0.06)]" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="mobile-bottom-nav">
            <div className="grid" style={{ gridTemplateColumns: `repeat(${bottomNav.length}, minmax(0, 1fr))` }}>
              {bottomNav.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`bottom-nav-${item.path === '/' ? 'home' : item.path.slice(1)}`}
                    className={`flex flex-col items-center justify-center gap-0.5 h-16 text-[11px] font-medium transition-colors active:bg-slate-100 ${active ? 'text-brand-700' : 'text-slate-500'}`}
                  >
                    <span className={`h-8 w-14 rounded-full flex items-center justify-center transition-colors ${active ? 'bg-brand-100' : ''}`}>
                      <Icon className="h-5 w-5" />
                    </span>
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </div>
  );
};

export default Layout;
